import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getTwilioRegulatoryRequirements,
  getChannelTwilioRegulatory,
  presignTwilioRegulatoryDocument,
  submitTwilioRegulatory,
} from "../api/client";
import { getApiErrorDisplayMessage } from "../utils/channelSaveError";

async function putToS3(uploadUrl, file) {
  const CACHE_CONTROL = "public, max-age=31536000, immutable";
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "Cache-Control": CACHE_CONTROL,
    },
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Upload falhou (${res.status}) ${text}`.trim());
  }
}

/**
 * Coleta Address/Bundle + documentos para Telefonia Xpro quando o país exige compliance.
 */
const TwilioRegulatoryComplianceBlock = forwardRef(function TwilioRegulatoryComplianceBlock(
  {
    country = "BR",
    phoneNumber = "",
    channelId = null,
    enabled = true,
  },
  ref
) {
  const { t } = useTranslation("settings");
  const [loading, setLoading] = useState(false);
  const [requirements, setRequirements] = useState(null);
  const [endUserType, setEndUserType] = useState("business");
  const [payload, setPayload] = useState({});
  const [filesByType, setFilesByType] = useState({});
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getTwilioRegulatoryRequirements({
        country,
        numberType: "local",
        endUserType,
      });
      const req = data?.requirements || data;
      setRequirements(req || null);
      if (channelId) {
        const cur = await getChannelTwilioRegulatory(channelId);
        setExisting(cur || null);
        const prevPayload = cur?.request?.payload_json;
        if (prevPayload && typeof prevPayload === "object") {
          setPayload((p) => ({ ...p, ...prevPayload }));
        } else if (typeof prevPayload === "string") {
          try {
            setPayload((p) => ({ ...p, ...JSON.parse(prevPayload) }));
          } catch {
            /* ignore */
          }
        }
      }
    } catch (err) {
      setError(
        getApiErrorDisplayMessage(err, "channelForm.twilioPhone.regulatoryLoadError")
      );
    } finally {
      setLoading(false);
    }
  }, [enabled, country, endUserType, channelId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const required = Boolean(requirements?.required);
  const fields = useMemo(() => requirements?.fields || [], [requirements]);
  const documents = useMemo(() => requirements?.documents || [], [requirements]);
  const existingDocs = existing?.documents || [];
  const requestStatus = existing?.request?.status || null;
  const alreadySubmitted = [
    "submitted_api",
    "pending_manual",
    "in_review",
    "approved",
    "collecting",
  ].includes(String(requestStatus || ""));

  const hasExistingDoc = (docType) =>
    existingDocs.some(
      (d) => String(d.docType || d.doc_type || "") === String(docType)
    );

  const validate = useCallback(() => {
    if (!enabled || !required || alreadySubmitted) return null;
    for (const f of fields) {
      if (f.required && !String(payload[f.key] || "").trim()) {
        return t("channelForm.twilioPhone.regulatoryFieldRequired", {
          defaultValue: "Preencha o campo obrigatório: {{label}}",
          label: f.label || f.key,
        });
      }
    }
    for (const doc of documents) {
      if (!doc.required) continue;
      if (!filesByType[doc.type] && !hasExistingDoc(doc.type)) {
        return t("channelForm.twilioPhone.regulatoryDocRequired", {
          defaultValue: "Anexe o documento: {{label}}",
          label: doc.label || doc.type,
        });
      }
    }
    return null;
  }, [
    enabled,
    required,
    alreadySubmitted,
    fields,
    documents,
    payload,
    filesByType,
    existingDocs,
    t,
  ]);

  const handleSubmit = async () => {
    const missing = validate();
    if (missing) {
      setError(missing);
      throw new Error(missing);
    }
    if (alreadySubmitted && !Object.keys(filesByType).length) {
      return existing;
    }
    setSubmitting(true);
    setMessage(null);
    setError(null);
    try {
      const uploadedDocs = [];
      for (const doc of documents) {
        const file = filesByType[doc.type];
        if (!file) {
          if (doc.required && !hasExistingDoc(doc.type)) {
            throw new Error(
              t("channelForm.twilioPhone.regulatoryDocRequired", {
                defaultValue: "Anexe o documento: {{label}}",
                label: doc.label || doc.type,
              })
            );
          }
          continue;
        }
        const signed = await presignTwilioRegulatoryDocument({
          filename: file.name,
          contentType: file.type || "application/pdf",
        });
        if (!signed?.uploadUrl || !signed?.key) {
          throw new Error("Falha ao assinar upload do documento.");
        }
        await putToS3(signed.uploadUrl, file);
        uploadedDocs.push({
          docType: doc.type,
          friendlyName: doc.label || doc.type,
          filename: file.name,
          mime: file.type || "application/pdf",
          sizeBytes: file.size,
          s3Key: signed.key,
        });
      }

      const result = await submitTwilioRegulatory(
        {
          phoneNumber,
          country,
          numberType: "local",
          endUserType,
          channelId: channelId || undefined,
          payload,
          documents: uploadedDocs,
          submit: true,
          requestId: existing?.request?.id || undefined,
        },
        channelId || null
      );

      const mode = result?.mode;
      const status = result?.status;
      if (mode === "manual" || status === "pending_manual") {
        setMessage(
          t("channelForm.twilioPhone.regulatoryPendingManual", {
            defaultValue:
              "Documentos recebidos. A equipe Xvision concluirá o processo e você será avisado quando for aprovado.",
          })
        );
      } else {
        setMessage(
          t("channelForm.twilioPhone.regulatorySubmittedApi", {
            defaultValue:
              "Documentos enviados. Aguardando aprovação — depois disso você poderá comprar o número.",
          })
        );
      }
      const nextExisting = {
        request: result?.request || {
          id: result?.requestId,
          status: status || (mode === "manual" ? "pending_manual" : "submitted_api"),
        },
        documents: result?.documents || uploadedDocs,
      };
      if (channelId) {
        const cur = await getChannelTwilioRegulatory(channelId);
        setExisting(cur || nextExisting);
      } else {
        setExisting(nextExisting);
      }
      return result;
    } catch (err) {
      setError(
        getApiErrorDisplayMessage(err, "channelForm.twilioPhone.regulatorySubmitError")
      );
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      isRequired: () => required,
      isApproved: () => String(requestStatus) === "approved",
      isLoading: () => loading,
      validate,
      submit: handleSubmit,
    }),
    [required, requestStatus, loading, validate, handleSubmit]
  );

  const setField = (key, value) => {
    setPayload((prev) => ({ ...prev, [key]: value }));
  };

  const onPickFile = (docType, file) => {
    if (!file) return;
    setFilesByType((prev) => ({ ...prev, [docType]: file }));
  };

  if (!enabled) return null;
  if (loading) {
    return (
      <Box sx={{ py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (!required) return null;

  const status = existing?.request?.status;

  return (
    <Grid item xs={12}>
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        {t("channelForm.twilioPhone.regulatoryRequiredAlert", {
          defaultValue:
            "Números neste país exigem documentação regulatória (endereço e documentos legais). Preencha os dados e anexe os documentos antes de comprar.",
        })}
      </Alert>

      {status ? (
        <Alert
          severity={
            status === "approved"
              ? "success"
              : status === "pending_manual" || status === "submitted_api"
                ? "info"
                : status === "rejected"
                  ? "error"
                  : "info"
          }
          sx={{ mb: 1.5 }}
        >
          {t(`channelForm.twilioPhone.regulatoryStatus_${status}`, {
            defaultValue: t("channelForm.twilioPhone.regulatoryStatus", {
              defaultValue: "Status da solicitação: {{status}}",
              status,
            }),
          })}
        </Alert>
      ) : null}

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {t("channelForm.twilioPhone.regulatoryTitle", {
          defaultValue: "Documentação regulatória",
        })}
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 1.5 }}>
        <RadioGroup
          row
          value={endUserType}
          onChange={(e) => setEndUserType(e.target.value)}
        >
          <FormControlLabel
            value="business"
            control={<Radio size="small" />}
            label={t("channelForm.twilioPhone.endUserBusiness", { defaultValue: "Empresa" })}
          />
          <FormControlLabel
            value="individual"
            control={<Radio size="small" />}
            label={t("channelForm.twilioPhone.endUserIndividual", { defaultValue: "Pessoa física" })}
          />
        </RadioGroup>
      </FormControl>

      <Grid container spacing={1.5}>
        {fields.map((f) => (
          <Grid item xs={12} sm={6} key={f.key}>
            <TextField
              size="small"
              fullWidth
              required={Boolean(f.required)}
              label={f.label || f.key}
              value={payload[f.key] || ""}
              onChange={(e) => setField(f.key, e.target.value)}
            />
          </Grid>
        ))}
      </Grid>

      <Stack spacing={1.25} sx={{ mt: 1.5 }}>
        {documents.map((doc) => (
          <Box key={doc.type}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {doc.label || doc.type}
              {doc.required ? " *" : ""}
            </Typography>
            <Button variant="outlined" size="small" component="label">
              {filesByType[doc.type]
                ? filesByType[doc.type].name
                : t("channelForm.twilioPhone.regulatoryPickFile", {
                    defaultValue: "Anexar PDF/JPG/PNG",
                  })}
              <input
                hidden
                type="file"
                accept={(doc.accept || ["application/pdf", "image/jpeg", "image/png"]).join(",")}
                onChange={(e) => onPickFile(doc.type, e.target.files?.[0])}
              />
            </Button>
          </Box>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
        <Button
          variant="contained"
          size="small"
          disabled={submitting}
          onClick={() => handleSubmit().catch(() => {})}
        >
          {submitting
            ? t("channelForm.twilioPhone.regulatorySubmitting", { defaultValue: "Enviando…" })
            : t("channelForm.twilioPhone.regulatorySubmit", {
                defaultValue: "Enviar documentos",
              })}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
        {t("channelForm.twilioPhone.regulatorySubmitHelper", {
          defaultValue:
            "Preencher os campos não compra o número. Envie os documentos (ou salve o canal) e, depois da aprovação, use «Comprar agora». Se o envio automático falhar, a equipe Xvision conclui o processo manualmente.",
        })}
      </Typography>

      {message ? (
        <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mt: 1.5, whiteSpace: "pre-line" }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
    </Grid>
  );
});

export default TwilioRegulatoryComplianceBlock;
